import React, { useContext, useEffect, useState } from "react";
import {
  Group,
  Pagination,
  Stack,
  Text,
  Tooltip,
  Badge,
  Loader,
} from "@mantine/core";
import { Navigate, NavLink, useLocation } from "react-router-dom";
import dayjs from "dayjs";
import DirectoryTable from "../../../themes/components/DirectoryTable";
import ProgressIndicator from "../../../themes/components/ProgressIndicator";
import ErrorPage from "../../../common/pages/ErrorPage";
import { AuthContext } from "../../../providers/AuthProvider";
import PayslipUploadHistoryTableEmptyStateContainer from "./PayslipUploadHistoryTableEmptyStateContainer";
import { payslipBadgeConfig, payslipBadgeLabel } from "../../../utils/Theme";

import DocumentProcessingJob, {
  DocumentProcessingJobStatus,
  DocumentProcessingJobWrapper,
  getDisplayStatus,
} from "../services/model/DocumentProcessingJob";
import { dateFormat, dateOnlyFormat } from "../../../utils/DateTime";
import { useEnabledFeatureFlag } from "../../../common/feature-flagging/FeatureFlagProvider";
import FeatureFlags from "../../../common/feature-flagging/FeatureFlags";
import ListItemButtons, {
  ListItemButtonProps,
} from "../../../themes/elements/ListItemButtons";
import CancelPayslipDocumentProcessingHook from "../hooks/CancelPayslipDocumentProcessingHook";
import FinalizePayslipDocumentProcessingHook from "../hooks/FinalizePayslipDocumentProcessingHook";
import ResponsiveHeight from "../../../utils/ResponsiveHeight";
import CancelFinalizedPayslipsHook from "../hooks/CancelFinalizedPayslipsHook";
import DocumentImporterConfiguration from "../services/model/DocumentImporterConfiguration";
import CustomTooltip from "../../../common/elements/CustomTooltip";
import Dictionary from "../../../utils/Dictionary";
import useDocumentUploadListApiService from "../services/api/DocumentUploadListApiService";
import { SupportedDocumentAttributes } from "../services/model/FeatureConfiguration";
import { AnalyticsContext } from "../../../providers/AnalyticsProvider";
import { useDocumentsListener } from "../../../providers/sse/DocumentListener";
import { MessageTypeTrigger } from "../../../providers/notifications/types";
import { useSnackbars } from "../../../providers/notifications/SnackbarsProvider";
import { useMessageTemplates } from "../../../providers/notifications/StrapiCMSApiService";
import UploadStatusTooltip from "./UploadStatusTooltip";

type PayslipUploadHistoryTableProps = {
  selection: DocumentProcessingJob[];
  onSelection: React.Dispatch<React.SetStateAction<DocumentProcessingJob[]>>;
  documentImporterConfiguration: DocumentImporterConfiguration;
  matchingJobIds?: Set<number>;
  searchBatches?: DocumentProcessingJob[];
};

const SETTLED_STATUSES = new Set([
  DocumentProcessingJobStatus.PARTIALLY_MATCHED,
  DocumentProcessingJobStatus.FAILED,
  DocumentProcessingJobStatus.PROCESSING_TIMEOUT,
  DocumentProcessingJobStatus.MATCHING_TIMEOUT,
  DocumentProcessingJobStatus.MATCHES_NEEDED,
  DocumentProcessingJobStatus.CANCELLED,
  DocumentProcessingJobStatus.MATCHED,
  DocumentProcessingJobStatus.FINALIZED,
]);

const PayslipUploadHistoryTable: React.FC<PayslipUploadHistoryTableProps> = ({
  selection,
  onSelection,
  documentImporterConfiguration,
  matchingJobIds,
  searchBatches,
}) => {
  const auth = useContext(AuthContext);
  const { trackDocumentsEvent } = useContext(AnalyticsContext);
  const { addSnackbar } = useSnackbars();
  const { data: messageTemplates } = useMessageTemplates();
  const location = useLocation();

  // State hooks grouped together
  const [activePage, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [documents, setDocuments] = useState<DocumentProcessingJobWrapper>({
    results: [],
    count: 0,
    pages: 0,
  });
  const [docActions, setDocActions] = useState<string[]>([]);
  const [pageLoaded, setPageLoaded] = useState(false);

  // Feature flags grouped together
  const flagPayslipsImportBulkActions = useEnabledFeatureFlag(
    FeatureFlags.PAYSLIPS_IMPORT_TABLE_BULK_ACTIONS
  );
  const flagPayslipsImportStatusRemove = useEnabledFeatureFlag(
    FeatureFlags.PAYSLIPS_IMPORT_TABLE_STATUS_REMOVE
  );
  const flagPayslipsCancelFinalized = useEnabledFeatureFlag(
    FeatureFlags.PAYSLIPS_CANCEL_FINALIZED
  );

  // Service hooks
  useDocumentsListener(
    setDocuments,
    documentImporterConfiguration.document_type
  );

  const isSearchActive =
    searchBatches !== undefined && searchBatches.length > 0;

  const { isLoading, isError, error, data } = useDocumentUploadListApiService({
    page: activePage,
    pagination: true,
    document_type: documentImporterConfiguration.document_type,
  });

  const { cancelDocument } = CancelPayslipDocumentProcessingHook(
    documentImporterConfiguration.document_name
  );
  const { finalizeDocument } = FinalizePayslipDocumentProcessingHook(
    documentImporterConfiguration.document_name
  );
  const { cancelFinalizedPayslips } = CancelFinalizedPayslipsHook(
    documentImporterConfiguration.document_name_plural
  );

  const tableMaxHeight = ResponsiveHeight(
    "",
    "80vh",
    "80vh",
    "80vh",
    "75vh",
    "80vh"
  );

  // Effect hooks with conditions moved inside
  useEffect(() => {
    setLoading(isLoading);
  }, [isLoading]);

  useEffect(() => {
    if (data !== undefined) {
      setDocuments(data[documentImporterConfiguration.document_type]);
    }
  }, [data, documentImporterConfiguration.document_type]);

  useEffect(() => {
    const processDocuments = (): void => {
      if (documents?.results?.length === 0 || pageLoaded) {
        return;
      }

      const docsReadyToReleaseIds =
        documents?.results
          ?.filter(
            (doc: DocumentProcessingJob) =>
              doc.status === DocumentProcessingJobStatus.MATCHED
          )
          .map((doc) => doc.id) || [];

      if (docsReadyToReleaseIds.length > 0) {
        const template = messageTemplates?.data.find(
          (t) =>
            t.attributes.resource_type ===
              documentImporterConfiguration.document_type &&
            t.attributes.trigger === MessageTypeTrigger.PageLoad &&
            t.attributes.message_type === "snackbar"
        );

        if (template) {
          const addedSnackbar = addSnackbar({
            id: `release_batch-[${docsReadyToReleaseIds.join(",")}]`,
            data: {
              number_of_unfinalised: docsReadyToReleaseIds.length,
            },
            template,
            type: "info",
            expiresAt: new Date(Date.now() + 600000),
            maxShowCount: 3,
            showCount: 0,
          });

          if (addedSnackbar) {
            trackDocumentsEvent({
              event: "Batch_Unsent_Reminder_Snackbar",
              description:
                "User receives a snackbar to remind them of unsent batches (after log in or x amount fo time)",
              event_type: "Snackbar",
              job: documents?.results[0],
              type: documentImporterConfiguration.document_name_plural,
            });
          }

          setPageLoaded(true);
        }
      }
    };

    processDocuments();
  }, [
    messageTemplates,
    documents,
    pageLoaded,
    addSnackbar,
    trackDocumentsEvent,
    documentImporterConfiguration,
  ]);

  useEffect(() => {
    const processLocationSearch = (): void => {
      const params = new URLSearchParams(location.search);
      const action = params.get("action");
      if (!action) {
        return;
      }

      const [actionType, ids] = action.split("-");
      let documentIds: string[] = [];
      try {
        documentIds = JSON.parse(ids);
      } catch (e) {
        documentIds = [];
      }

      if (["fix", "release", "release_batch"].includes(actionType)) {
        setDocActions(documentIds.map((id) => `${actionType}-${id}`));
      }
    };

    processLocationSearch();
  }, [location.search]);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    if (docActions.length > 0) {
      timeoutId = setTimeout(() => {
        setDocActions([]);
      }, 6000);
    }
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [docActions]);

  if (!auth) {
    return <Navigate replace to="/login" />;
  }

  if (
    !isSearchActive &&
    (loading || !data || !data[documentImporterConfiguration.document_type])
  ) {
    return <ProgressIndicator />;
  }

  if (!isSearchActive && isError) {
    return <ErrorPage errors={error} />;
  }

  if (!isSearchActive && (!documents || !documents.results.length)) {
    return (
      <PayslipUploadHistoryTableEmptyStateContainer
        documentImporterConfiguration={documentImporterConfiguration}
      />
    );
  }

  const cancelDocumentProcess = (doc: DocumentProcessingJob): void => {
    cancelDocument(doc.id);
  };

  const approveAndRelease = (doc: DocumentProcessingJob): void => {
    finalizeDocument(auth.employer.id, doc.id);
  };

  const renderActions = (
    doc: DocumentProcessingJob
  ): { elements: ListItemButtonProps[] } => {
    const isStaffMember = auth?.is_staff ?? false;
    const canDownloadBatchPayslip = isStaffMember && !!doc.file_to_process;

    const sendAction: ListItemButtonProps = {
      id: "finalize-payslip-upload",
      verify: {
        show: true,
        message: "Are you sure you want to send this upload?",
        button: "Send",
        callback: () => {
          trackDocumentsEvent(
            {
              event: `Approve_Release_Batch_CTA`,
              description: `User clicks on the approve and release button of an uploaded ${documentImporterConfiguration.document_name_lower_case} batch`,
              event_type: "CTA:Click",
              type: documentImporterConfiguration.document_name_plural,
              job: doc,
            },
            {
              Approve_Release_Batch_Confirmed: null,
            }
          );
        },
      },
      callback: () => {
        trackDocumentsEvent(
          {
            event: `Approve_Release_Batch_CTA_Confirmed`,
            description: `User clicks on the approve and release button of an uploaded ${documentImporterConfiguration.document_name_lower_case} batch`,
            event_type: "CTA:Click",
            type: documentImporterConfiguration.document_name_plural,
            job: doc,
          },
          {
            Approve_Release_Batch_Confirmed: true,
          }
        );
        approveAndRelease(doc);
      },
      element: (
        <Tooltip
          label={
            <div>
              <strong>
                Send {documentImporterConfiguration.document_name_plural}
              </strong>
              <br />
              <span>
                Click the <strong>&apos;Send&apos;</strong> button to send out
                this {documentImporterConfiguration.document_name_lower_case}{" "}
                batch to employees.
              </span>
            </div>
          }
          placement="end"
          arrowSize={3}
          withArrow
          width={300}
          wrapLines
          disabled={
            ![
              DocumentProcessingJobStatus.MATCHED,
              DocumentProcessingJobStatus.PARTIALLY_MATCHED,
            ].includes(doc.status)
          }
        >
          <button
            type="button"
            id={`release-${doc.id}`}
            className={`px-2 py-1 text-xs text-white border border-[#062133] bg-[#062133] rounded-md hover:bg-[#ED7687] hover:border-[#ED7687] ${
              (docActions.includes(`release-${doc.id}`) ||
                docActions.includes(`release_batch-${doc.id}`)) &&
              [
                DocumentProcessingJobStatus.MATCHED,
                DocumentProcessingJobStatus.PARTIALLY_MATCHED,
              ].includes(doc.status) &&
              "animate-bounce"
            }`}
            style={{
              ...(![
                DocumentProcessingJobStatus.MATCHED,
                DocumentProcessingJobStatus.PARTIALLY_MATCHED,
              ].includes(doc.status) && {
                cursor: "not-allowed",
                color: "white",
                opacity: 0.35,
                backgroundColor: "rgba(6, 33, 51, 0.3)",
                borderColor: "rgba(6, 33, 51, 0.3)",
              }),
            }}
            disabled={
              ![
                DocumentProcessingJobStatus.MATCHED,
                DocumentProcessingJobStatus.PARTIALLY_MATCHED,
              ].includes(doc.status)
            }
          >
            Send
          </button>
        </Tooltip>
      ),
      active: [
        DocumentProcessingJobStatus.MATCHED,
        DocumentProcessingJobStatus.PARTIALLY_MATCHED,
      ].includes(doc.status),
    };

    const cancelAction: ListItemButtonProps = {
      id: "cancel-payslip-upload",
      verify: {
        show: true,
        message: ` ${
          doc.status === DocumentProcessingJobStatus.FINALIZED &&
          flagPayslipsCancelFinalized
            ? `Deleting finalized ${documentImporterConfiguration.document_name_plural_lower_case} will remove them from employees.`
            : ""
        } Are you sure you want to cancel this upload?`,
        button: "Confirm",
        callback: () => {
          trackDocumentsEvent(
            {
              event: `Cancel_Batch_CTA`,
              description: `User clicks on the cancel button of an uploaded ${documentImporterConfiguration.document_name_lower_case} batch`,
              event_type: "CTA:Click",
              type: documentImporterConfiguration.document_name_plural,
              job: doc,
            },
            {
              Cancelled_Batch_Confirmed: null,
            }
          );
        },
      },
      callback: () => {
        trackDocumentsEvent(
          {
            event: `Cancel_Batch_CTA_Confirmed`,
            description: `User clicks on the cancel button of an uploaded ${documentImporterConfiguration.document_name_lower_case} batch`,
            event_type: "CTA:Click",
            type: documentImporterConfiguration.document_name_plural,
            job: doc,
          },
          {
            Cancelled_Batch_Confirmed: true,
          }
        );

        // Use an if-else statement to clearly define the conditional execution
        if (
          doc.status === DocumentProcessingJobStatus.FINALIZED &&
          flagPayslipsCancelFinalized
        ) {
          cancelFinalizedPayslips(doc.id);
        } else {
          cancelDocumentProcess(doc);
        }
      },
      element: (
        <button
          type="button"
          className="px-2 py-1 text-xs text-[#062133] bg-white border border-[#062133] rounded-md hover:bg-gray-100 hover:text-gray-800"
          style={{
            ...(![
              DocumentProcessingJobStatus.MATCHED,
              DocumentProcessingJobStatus.PARTIALLY_MATCHED,
              DocumentProcessingJobStatus.MATCHES_NEEDED,
              DocumentProcessingJobStatus.MATCHING_TIMEOUT,
              DocumentProcessingJobStatus.PROCESSING_TIMEOUT,
              flagPayslipsCancelFinalized &&
                DocumentProcessingJobStatus.FINALIZED,
            ].includes(doc.status) && {
              cursor: "not-allowed",
              borderColor: "rgba(6, 33, 51, 0.3)",
              color: "rgba(6, 33, 51, 0.3)",
            }),
          }}
          disabled={
            ![
              DocumentProcessingJobStatus.MATCHED,
              DocumentProcessingJobStatus.PARTIALLY_MATCHED,
              DocumentProcessingJobStatus.MATCHES_NEEDED,
              DocumentProcessingJobStatus.MATCHING_TIMEOUT,
              DocumentProcessingJobStatus.PROCESSING_TIMEOUT,
              flagPayslipsCancelFinalized &&
                DocumentProcessingJobStatus.FINALIZED,
            ].includes(doc.status)
          }
        >
          Cancel
        </button>
      ),
      active: [
        DocumentProcessingJobStatus.MATCHED,
        DocumentProcessingJobStatus.PARTIALLY_MATCHED,
        DocumentProcessingJobStatus.MATCHES_NEEDED,
        DocumentProcessingJobStatus.MATCHING_TIMEOUT,
        DocumentProcessingJobStatus.PROCESSING_TIMEOUT,
        flagPayslipsCancelFinalized && DocumentProcessingJobStatus.FINALIZED,
      ].includes(doc.status),
    };

    const downloadAction: ListItemButtonProps = {
      id: "download-batch-file",
      verify: {
        show: canDownloadBatchPayslip, // Must be true for callback to be executed
        message: "", // Empty message for minimal UI
        button: "Download", // Button text for confirmation dialog
        callback: () => {
          trackDocumentsEvent({
            event: `Download_Batch_File_CTA`,
            description: `User clicks on the download button to get the batch file of an uploaded ${documentImporterConfiguration.document_name_lower_case}`,
            event_type: "CTA:Click",
            type: documentImporterConfiguration.document_name_plural,
            job: doc,
          });

          if (doc.file_to_process) {
            window.open(doc.file_to_process, "_blank");
            setTimeout(() => {
              const closeButton = document.querySelector(
                ".mantine-Modal-close"
              );
              if (closeButton) {
                (closeButton as HTMLElement).click();
              }
            }, 100);
          }
        },
      },
      callback: () => {
        trackDocumentsEvent(
          {
            event: `Download_Batch_File_CTA_Confirmed`,
            description: `User clicks on the download button of an uploaded ${documentImporterConfiguration.document_name_lower_case} batch`,
            event_type: "CTA:Click",
            type: documentImporterConfiguration.document_name_plural,
            job: doc,
          },
          {
            Cancelled_Batch_Confirmed: true,
          }
        );
      },
      element: (
        <Tooltip
          label={
            <div>
              <strong>Download Batch File</strong>
              <br />
              <span>
                Click the <strong>&apos;Download&apos;</strong> button to
                download the original batch file.
              </span>
            </div>
          }
          placement="end"
          arrowSize={3}
          withArrow
          width={300}
          wrapLines
          disabled={!canDownloadBatchPayslip}
        >
          <button
            type="button"
            id={`download-${doc.id}`}
            className="px-2 py-1 text-xs text-[#062133] bg-white border border-[#062133] rounded-md hover:bg-gray-100 hover:text-gray-800"
            style={{
              ...(!canDownloadBatchPayslip && {
                cursor: "not-allowed",
                borderColor: "rgba(6, 33, 51, 0.3)",
                color: "rgba(6, 33, 51, 0.3)",
              }),
            }}
            disabled={!canDownloadBatchPayslip}
          >
            Download
          </button>
        </Tooltip>
      ),
      active: canDownloadBatchPayslip,
    };

    const actions: ListItemButtonProps[] = [sendAction, cancelAction];

    if (isStaffMember) {
      actions.push(downloadAction);
    }

    return {
      elements: actions,
    };
  };

  // check if date created is before 44 minutes
  const checkIfDocumentProcessingJobIsBefore44Minutes = (
    time: string
  ): boolean =>
    time.includes("seconds") ||
    time.includes("minute") ||
    time.includes("minutes");

  // hide/remove document processing jobs stuck in the following states: Failed & Cancelled and processing time is before 44 minutes
  const isDocumentProcessingJobStuck = (
    documentProcessingJob: DocumentProcessingJob
  ): boolean => {
    if (
      documentProcessingJob.status === DocumentProcessingJobStatus.PROCESSING
    ) {
      return checkIfDocumentProcessingJobIsBefore44Minutes(
        dayjs(documentProcessingJob.date_created).fromNow(true)
      );
    }

    return ![
      DocumentProcessingJobStatus.CANCELLED,
      DocumentProcessingJobStatus.FAILED,
      DocumentProcessingJobStatus.PROCESSING_TIMEOUT,
    ].includes(documentProcessingJob.status);
  };

  const statusFilteredData = flagPayslipsImportStatusRemove
    ? documents?.results.filter((it: any) => isDocumentProcessingJobStuck(it))
    : documents?.results;

  const filteredData = isSearchActive ? searchBatches : statusFilteredData;

  const periodDatesColumnName =
    Dictionary[documentImporterConfiguration.document_type].period_dates_label;
  const showPeriodDates = documentImporterConfiguration.attributes.includes(
    SupportedDocumentAttributes.START_AND_END_PERIOD
  );

  const generateHeaders = (): (string | JSX.Element)[] => {
    const headerList: (string | JSX.Element)[] = ["Uploaded by", "Batch Name"];

    // Conditionally add either "Period Dates" or "Issue Date"
    if (showPeriodDates) {
      headerList.push(periodDatesColumnName);
    } else {
      headerList.push("Issue Date");
    }

    // Add other headers
    headerList.push("Distribution Date");
    headerList.push("Total");
    headerList.push("Assigned");

    headerList.push(
      <Stack spacing={4} key="errors-header" style={{ flexDirection: "row" }}>
        {Dictionary[documentImporterConfiguration.document_type].errors_heading}
        <CustomTooltip
          onMouseEnterCallBack={() => {
            trackDocumentsEvent({
              event: "Unmatched_Tooltip",
              description:
                "User hovers over unmatched tooltip (?) and the tooltip renders",
              event_type: "TooltipView",
              type: documentImporterConfiguration.document_name_plural,
              job: null,
            });
          }}
          label={
            Dictionary[documentImporterConfiguration.document_type]
              .errors_heading_tooltip
          }
        />
      </Stack>
    );

    headerList.push("Status");
    headerList.push("Actions");

    return headerList;
  };

  const getDateColumnValue = (it: any): string => {
    if (showPeriodDates) {
      const extraAttributes = it.extra_attributes;

      if (extraAttributes) {
        const startDate: string = extraAttributes.start_date
          ? dateOnlyFormat(extraAttributes.start_date)
          : "N/A";
        const endDate: string = extraAttributes.end_date
          ? dateOnlyFormat(extraAttributes.end_date)
          : "N/A";
        return `${startDate} - ${endDate}`;
      }

      return "";
    }

    return dateOnlyFormat(it.date_issued);
  };

  const headers = generateHeaders();

  return (
    <>
      <DirectoryTable
        tableMaxHeight={tableMaxHeight}
        data={filteredData}
        selection={selection}
        onSelect={onSelection}
        hideSelectControls={!flagPayslipsImportBulkActions}
        headers={headers}
        headersStyles={{
          Total: { width: "25px" },
          Matched: { width: "25px" },
          Errors: { width: "25px" },
        }}
        rows={filteredData?.map((it: any, index: number) => {
          const displayStatus = getDisplayStatus(it.status, it.status_reason);
          return {
            name: (
              <Group spacing="sm">
                <Text size="sm">
                  {`${it.uploaded_by?.first_name} ${
                    it.uploaded_by && it.uploaded_by?.last_name
                      ? it.uploaded_by?.last_name[0]
                      : ""
                  }.`}
                </Text>
              </Group>
            ),
            batchName: (
              <Tooltip
                label={it.batch_name}
                placement="start"
                arrowSize={3}
                withArrow
                width={200}
                wrapLines
              >
                <span>
                  {it.batch_name.length > 12
                    ? `${it.batch_name.substring(0, 12)}...`
                    : it.batch_name}
                </span>
              </Tooltip>
            ),
            issueOrPeriodDates: getDateColumnValue(it),
            distributionDate: dateFormat(it.date_of_distribution),
            total: it.documents_total_count,
            match: it.documents_matched_count,
            errors:
              (it.documents_failed_count && (
                <div
                  id={`fix-${it.id}`}
                  className={`${
                    docActions.includes(`fix-${it.id}`) && "animate-bounce"
                  } underline`}
                >
                  <NavLink
                    id={`payslips-table-errors-link${index}`}
                    to={`${it.id}/errors`}
                    style={{
                      color: "#062133",
                    }}
                    onClick={() => {
                      trackDocumentsEvent({
                        event: `Match_${documentImporterConfiguration.document_name_plural}_CTA`,
                        description: `User clicks on the "match ${it.documents_failed_count}" hyperlink CTA`,
                        event_type: "CTA:Click",
                        type: documentImporterConfiguration.document_name_plural,
                        job: it,
                      });
                    }}
                    state={{ jobStatus: it.status }}
                  >
                    {[
                      DocumentProcessingJobStatus.MATCHED,
                      DocumentProcessingJobStatus.PARTIALLY_MATCHED,
                      DocumentProcessingJobStatus.FINALIZED,
                      DocumentProcessingJobStatus.MATCHES_NEEDED,
                    ].includes(it.status)
                      ? `Assign ${it.documents_failed_count}`
                      : it.documents_failed_count}
                  </NavLink>
                </div>
              )) ||
              0,
            status: (
              <Badge
                id={`payslips-table-status${index}`}
                style={{
                  color: "rgba(6, 33, 51, 0.8)",
                  backgroundColor: payslipBadgeConfig[displayStatus || ""],
                  textTransform: "uppercase",
                  height: "fit-content",
                }}
                color={payslipBadgeConfig[displayStatus || ""]}
                styles={{
                  root: {
                    paddingLeft: "0",
                    paddingRight: "0",
                  },
                  inner: {
                    overflow: "unset",
                    display: "flex",
                    alignItems: "center",
                  },
                }}
              >
                {!SETTLED_STATUSES.has(it.status) && (
                  <div className="pl-2">
                    <Loader color="rgba(65, 136, 171, 1)" size={15} />
                  </div>
                )}
                <span style={{ paddingLeft: "4px", paddingRight: "2px" }}>
                  {(payslipBadgeLabel[displayStatus] || displayStatus).replace(
                    "{document_type}",
                    documentImporterConfiguration.document_name_plural_lower_case
                  )}
                </span>
                <UploadStatusTooltip
                  status={displayStatus}
                  statusReason={it.status_reason}
                />
              </Badge>
            ),
            actions: <ListItemButtons {...renderActions(it)} />,
          };
        })}
      />

      {!isSearchActive && filteredData.length > 1 && documents.pages > 1 && (
        <Pagination
          position="right"
          mt={16}
          onChange={setPage}
          total={documents.pages}
          initialPage={1}
          page={activePage}
        />
      )}
    </>
  );
};

export default PayslipUploadHistoryTable;
